/**
 * OrgSelectorContext
 *
 * Provides a global "selected organization" that SuperAdmins can switch via
 * the navbar dropdown.  Non-SuperAdmins always see their own org (read-only).
 *
 * Usage:
 *   const { selectedOrgId, setSelectedOrgId, orgs, isLoading } = useOrgSelector()
 *
 * For API calls use the convenience hook:
 *   const orgFilter = useOrgFilter()   // returns { orgId?: string }
 *   useQuery(['leads', orgFilter], () => leadsApi.getAll(orgFilter))
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { authStore } from '../lib/stores/authStore'
import { orgsApi } from '../lib/api/tenantsApi'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OrgOption {
  id:   string
  name: string
}

interface OrgSelectorState {
  /** null = "All Organizations" (SuperAdmin only). Non-SuperAdmin: always their org. */
  selectedOrgId:   string | null
  selectedOrgName: string
  orgs:            OrgOption[]
  isLoading:       boolean
  isSuperAdmin:    boolean
  setSelectedOrgId: (id: string | null) => void
}

// ── Context ───────────────────────────────────────────────────────────────────

const OrgSelectorContext = createContext<OrgSelectorState>({
  selectedOrgId:    null,
  selectedOrgName:  'All Organizations',
  orgs:             [],
  isLoading:        false,
  isSuperAdmin:     false,
  setSelectedOrgId: () => {},
})

// ── Storage key ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'crm_selected_org_id'

function readPersistedOrgId(): string | null {
  try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
}

function persistOrgId(id: string | null) {
  try {
    if (id === null) localStorage.removeItem(STORAGE_KEY)
    else             localStorage.setItem(STORAGE_KEY, id)
  } catch { /* ignore */ }
}

// ── Provider ──────────────────────────────────────────────────────────────────

// A GUID claim is only "real" if it's present and not the zero-GUID the backend
// sometimes sends for "unset". Mirrors Topbar's OrgBadge.isRealId and the backend's
// ITenantContext convention (IsRealSuperAdmin = OrgId == Guid.Empty).
const ZERO_GUID = '00000000-0000-0000-0000-000000000000'
function isRealId(id?: string | null): boolean {
  return !!id && id !== ZERO_GUID
}

export const OrgSelectorProvider = observer(({ children }: { children: ReactNode }) => {
  // Super Admin = no OrgId AND no LobId assigned. Don't rely on the roleName string
  // (e.g. "SUPER_ADMIN") matching exactly — OrgId/LobId nullity is the source of truth,
  // same as the backend's TenantContext, so a Super Admin is never accidentally treated
  // as a tenant-scoped user just because their role label doesn't match.
  const isSuperAdmin = !isRealId(authStore.user?.orgId) && !isRealId(authStore.user?.lobId)
  const queryClient = useQueryClient()

  // ── Fetch orgs list (SuperAdmin only) ─────────────────────────────────────
  const { data: fetchedOrgs = [], isLoading } = useQuery({
    queryKey:  ['orgs-for-selector'],
    queryFn:   orgsApi.getAll,
    enabled:   isSuperAdmin && authStore.isAuthenticated,
    staleTime: 5 * 60_000,
    select:    (orgs) => orgs.map(o => ({ id: o.id, name: o.name })),
  })

  // ── Selected org ID ───────────────────────────────────────────────────────
  const [selectedOrgId, _setSelectedOrgId] = useState<string | null>(() => {
    return readPersistedOrgId()     // Restore from localStorage unconditionally
  })

  // When orgs load, verify the persisted selection is still valid
  useEffect(() => {
    if (!isSuperAdmin || fetchedOrgs.length === 0) return
    if (selectedOrgId !== null && !fetchedOrgs.find(o => o.id === selectedOrgId)) {
      _setSelectedOrgId(null)
      persistOrgId(null)
    }
  }, [fetchedOrgs, isSuperAdmin, selectedOrgId])

  const setSelectedOrgId = useCallback((id: string | null) => {
    _setSelectedOrgId(id)
    persistOrgId(id)
    queryClient.invalidateQueries()
  }, [queryClient])

  // ── Derived: display name ─────────────────────────────────────────────────
  const selectedOrgName =
    isSuperAdmin
      ? selectedOrgId === null
        ? 'All Organizations'
        : (fetchedOrgs.find(o => o.id === selectedOrgId)?.name ?? 'Unknown Org')
      : (authStore.user?.orgName ?? authStore.user?.orgId ?? 'My Organization')

  return (
    <OrgSelectorContext.Provider
      value={{
        selectedOrgId,
        selectedOrgName,
        orgs:        fetchedOrgs,
        isLoading,
        isSuperAdmin,
        setSelectedOrgId,
      }}
    >
      {children}
    </OrgSelectorContext.Provider>
  )
})

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useOrgSelector() {
  return useContext(OrgSelectorContext)
}

/**
 * Returns the org filter object to spread into API call params.
 *
 * SuperAdmin + specific org selected → { orgId: 'uuid' }
 * SuperAdmin + "All Organizations"   → {}               (no filter)
 * Non-SuperAdmin                     → {}               (backend enforces via JWT)
 */
export function useOrgFilter(): { orgId?: string } {
  const { selectedOrgId, isSuperAdmin } = useContext(OrgSelectorContext)
  if (isSuperAdmin && selectedOrgId !== null) return { orgId: selectedOrgId }
  return {}
}
